import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, TablePagination
} from '@mui/material';
import { leaveBalanceService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

const EmployeeLeaveBalance: React.FC = () => {
  const [balances, setBalances] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [adjustData, setAdjustData] = useState({
    leaveType: 'Sick Leave',
    amount: 0,
    remarks: ''
  });

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      const data = await leaveBalanceService.getAllBalances();
      setBalances(data);
    } catch (error) {
      console.error('Error loading balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreditMonthly = async () => {
    try {
      const response = await leaveBalanceService.creditMonthly();
      setMessage(response.message || 'Monthly leaves credited successfully');
      loadBalances();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error crediting leaves');
    }
  };

  const handleAdjustClick = (user: any) => {
    setSelectedUser(user);
    setAdjustDialog(true);
  };

  const handleAdjustBalance = async () => {
    if (!selectedUser) return;
    try {
      const response = await leaveBalanceService.adjustBalance(
        selectedUser.userId,
        adjustData.leaveType,
        adjustData.amount,
        adjustData.remarks
      );
      setMessage(response.message || 'Balance adjusted successfully');
      setAdjustDialog(false);
      setAdjustData({ leaveType: 'Sick Leave', amount: 0, remarks: '' });
      loadBalances();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error adjusting balance');
    }
  };

  const handleRevokeLastCredit = async (userId: number, leaveType: string) => {
    if (window.confirm(`Revoke last monthly ${leaveType} credit for this employee?`)) {
      try {
        const response = await leaveBalanceService.revokeLastCredit(userId, leaveType);
        setMessage(response.message || 'Last credit revoked successfully');
        loadBalances();
        setTimeout(() => setMessage(''), 3000);
      } catch (error: any) {
        setMessage(error.response?.data?.message || 'Error revoking credit');
      }
    }
  };

  // Called unconditionally (rules of hooks) even before the `loading` early return.
  const { pagedRows: pagedBalances, paginationProps: balancesPaginationProps } = usePagination(balances);

  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Employee Leave Balances</Typography>
        <Button variant="contained" color="primary" onClick={handleCreditMonthly}>
          Credit Monthly Leaves
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Sick Leave, Earned Leave and Comp Off Leave share a single balance (1.5 days credited
        per month). The "SL"/"EL" split below under Credit/Debit is just a label on each
        transaction for reporting — it no longer reflects separate buckets.
      </Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee Name</TableCell>
              <TableCell align="right">Available Balance</TableCell>
              <TableCell align="right">Total Credit (YTD)</TableCell>
              <TableCell align="right">Total Debit (YTD)</TableCell>
              <TableCell align="right">Total Balance</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedBalances.map((balance) => (
              <TableRow key={balance.userId}>
                <TableCell>{balance.userName}</TableCell>
                <TableCell align="right">{balance.totalBalance}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>
                  {balance.totalCredit}
                  <Typography variant="caption" display="block" color="text.secondary">
                    SL: {balance.creditSickLeave}, EL: {balance.creditEarnedLeave}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  {balance.totalDebit}
                  <Typography variant="caption" display="block" color="text.secondary">
                    SL: {balance.debitSickLeave}, EL: {balance.debitEarnedLeave}
                  </Typography>
                </TableCell>
                <TableCell align="right"><strong>{balance.totalBalance}</strong></TableCell>
                <TableCell align="right">
                  {(balance.pendingSickLeave > 0 || balance.pendingEarnedLeave > 0)
                    ? `SL: ${balance.pendingSickLeave}, EL: ${balance.pendingEarnedLeave}`
                    : '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => handleAdjustClick(balance)}>
                      Adjust
                    </Button>
                    <Button size="small" variant="outlined" color="warning" onClick={() => handleRevokeLastCredit(balance.userId, 'Sick Leave')}>
                      Revoke SL
                    </Button>
                    <Button size="small" variant="outlined" color="warning" onClick={() => handleRevokeLastCredit(balance.userId, 'Earned Leave')}>
                      Revoke EL
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {balances.length > 0 && <TablePagination {...balancesPaginationProps} />}
      </TableContainer>

      <Dialog open={adjustDialog} onClose={() => setAdjustDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Leave Balance - {selectedUser?.userName}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Leave Type"
            value={adjustData.leaveType}
            onChange={(e) => setAdjustData({ ...adjustData, leaveType: e.target.value })}
            margin="normal"
          >
            <MenuItem value="Sick Leave">Sick Leave</MenuItem>
            <MenuItem value="Earned Leave">Earned Leave</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Amount (+ to add, - to deduct)"
            type="number"
            value={adjustData.amount}
            onChange={(e) => setAdjustData({ ...adjustData, amount: parseFloat(e.target.value) || 0 })}
            margin="normal"
            helperText="Use positive number to credit, negative to debit. Decimals allowed (e.g., 0.5, 1.25)"
            inputProps={{ step: 0.25 }}
          />
          <TextField
            fullWidth
            label="Remarks"
            value={adjustData.remarks}
            onChange={(e) => setAdjustData({ ...adjustData, remarks: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialog(false)}>Cancel</Button>
          <Button onClick={handleAdjustBalance} variant="contained">Adjust Balance</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeLeaveBalance;
